"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPEventCellUpdated = void 0;
const compute_manager_1 = require("../../../compute_manager");
const helper_1 = require("../../../event_manager/helper");
const model_1 = require("../../../model");
const store_1 = require("../../../exports/store");
const types_1 = require("../../../types");
const resource_types_1 = require("../../../types/resource_types");
const interface_1 = require("../interface");
const const_1 = require("./../../const");
// @EventMeta(OPEventNameEnums.CellUpdated)
class OPEventCellUpdated extends interface_1.IAtomEventType {
    constructor() {
        super(...arguments);
        this.eventName = const_1.OPEventNameEnums.CellUpdated;
        this.realType = const_1.EventRealTypeEnums.REAL;
        this.scope = resource_types_1.ResourceType.Datasheet;
    }
    test(opContext) {
        const { action, resourceId, resourceType } = opContext;
        if (resourceType !== resource_types_1.ResourceType.Datasheet) {
            return {
                pass: false,
                context: null,
            };
        }
        const { pass, recordId, fieldId } = (0, helper_1.testPath)(action.p, ['recordMap', ':recordId', 'data', ':fieldId']);
        return {
            pass,
            context: {
                recordId,
                fieldId,
                datasheetId: resourceId,
                action,
                change: {
                    from: action['od'],
                    to: action['oi'],
                }
            }
        };
    }
    /**
     * Entity events generate virtual events, deduplication.
     * This relies on meta and recordMap , so you need to pass in state.
     */
    computeEvent(eventBuffer, state) {
        const computeRefManager = (0, compute_manager_1.getComputeRefManager)(state);
        const computeEventContextQueue = [];
        const finalComputeEventContextQueue = [];
        const enqueueChecker = (key) => {
            if (!computeEventContextQueue.includes(key)) {
                computeEventContextQueue.push(key);
                finalComputeEventContextQueue.push(key);
            }
        };
        const addComputeEventContext = (context, state) => {
            const [datasheetId, recordId, fieldId] = context.split('-');
            // 1. A cell is updated, this cell corresponds to the field
            const updateCellField = store_1.Selectors.getField(state, fieldId, datasheetId);
            if (!updateCellField) {
                return;
            }
            // 2. All fields that depend on this field (these fields are all calculated fields)
            const fieldRefs = computeRefManager.refMap.get(`${datasheetId}-${fieldId}`);
            if (!(fieldRefs === null || fieldRefs === void 0 ? void 0 : fieldRefs.size)) {
                return;
            }
            fieldRefs.forEach(refId => {
                const [_datasheetId, _fieldId] = refId.split('-');
                const fieldMap = store_1.Selectors.getFieldMap(state, _datasheetId);
                // 3. Depends on one of the fields of this field
                const field = fieldMap[_fieldId];
                // FIXME: There is a fieldId that does not exist in fieldMap, 
                // the field has been deleted, and the reference relationship of refMap has not been updated in time.
                if (!field)
                    return;
                const isEventInSameDatasheet = _datasheetId === datasheetId;
                const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
                // 4. The table that triggers the event and the table that depends on this event are different tables, 
                // which can generate cross-table dependencies, which must be the lookup or link fields.
                switch (field.type) {
                    case types_1.FieldType.LookUp:
                        // 1. The link field that the lookup field depends on
                        const relatedLinkField = model_1.Field.bindContext(field, state).getRelatedLinkField();
                        if (!relatedLinkField) {
                            return;
                        }
                        let triggerRecIds = [];
                        // 2. The same table triggers a lookup update, which must be associated with the table. 
                        // The sibling field of the associated link field is itself
                        if (isEventInSameDatasheet) {
                            // Cause the lookup field to change, either the link field or the entity field being looked.
                            if (updateCellField.id === relatedLinkField.id) {
                                enqueueChecker(`${_datasheetId}-${recordId}-${_fieldId}`);
                            }
                            else {
                                // Since the entity field of the table association look changes. 
                                // The lookup fields of all records that reference this record are updated.
                                triggerRecIds = Object.keys(snapshot.recordMap).reduce((prev, _recordId) => {
                                    const linkCellValue = store_1.Selectors.getCellValue(state, snapshot, _recordId, relatedLinkField.id);
                                    if (linkCellValue && linkCellValue.includes(recordId)) {
                                        prev.push(_recordId);
                                    }
                                    return prev;
                                }, []);
                            }
                        }
                        else {
                            const brotherFieldId = relatedLinkField.property.brotherFieldId;
                            // 3. The recordIds affected by this cell update
                            triggerRecIds = store_1.Selectors.getCellValue(state, snapshot, recordId, brotherFieldId);
                        }
                        // TODO: The value of the link field cell must be null or an array. 
                        // Due to the existence of dirty data, we first judge whether it is an array type before processing it. Delete data after cleaning?
                        if (triggerRecIds && Array.isArray(triggerRecIds)) {
                            triggerRecIds.forEach(recId => {
                                enqueueChecker(`${_datasheetId}-${recId}-${_fieldId}`);
                            });
                        }
                        break;
                    case types_1.FieldType.Formula:
                        enqueueChecker(`${_datasheetId}-${recordId}-${_fieldId}`);
                        break;
                    case types_1.FieldType.Link:
                        // The LINK field is not a strictly calculated field, 
                        // and the title of the link field needs to be updated here. Temporarily treated as a calculated field
                        // The link field in the update table this time
                        const thisLinkFieldId = field.property.brotherFieldId;
                        if (!thisLinkFieldId)
                            return;
                        // Which records of the foreign key table depend on this record
                        const linkRecIds = store_1.Selectors.getCellValue(state, snapshot, recordId, thisLinkFieldId);
                        // FIXME: There may be a non-empty cv that is not an array here. Causes the following code to have problems, first compatible.
                        if (linkRecIds && Array.isArray(linkRecIds)) {
                            linkRecIds.forEach(recId => {
                                enqueueChecker(`${_datasheetId}-${recId}-${_fieldId}`);
                            });
                        }
                        break;
                    default:
                        // TODO: Include creation time, creator, update time, update person, 
                        // and auto-increment numbers into event management. 10.13 Modifications after launch
                        console.warn('! ' + `Unknown computed field type: ${field.type}`);
                    // throw new Error(`unsupported field type: ${field.type}`);
                }
            });
        };
        // The context from which the entity event came
        eventBuffer.forEach(({ context }) => {
            const { datasheetId, recordId, fieldId } = context;
            const key = `${datasheetId}-${recordId}-${fieldId}`;
            addComputeEventContext(key, state);
        });
        // The context from which the virtual event came
        while (computeEventContextQueue.length) {
            const context = computeEventContextQueue[0];
            computeEventContextQueue.shift();
            addComputeEventContext(context, state);
        }
        // After set deduplication. Inside the computeRef is the update of the calculation cell that will be triggered by the update of the entity cell.
        /**
         * !!! The collection continues to push new elements into the collection during the loop. New elements also appear in this cycle.
         * const aset = new Set([1,2,3])
         * aset.forEach(i=> {
         *   console.log(i);
         *   if(i====3){
         *     aset.add(4)
         *   }
         * })
         * output: 1 2 3 4
         * Here, when the signal is calculated in a loop, the event will be triggered recursively,
         * and the event will push the signal into the collection. until the signal is empty.
         * In theory, in the absence of circular references, there will be no infinite loop.
         */
        const res = [];
        finalComputeEventContextQueue.forEach(context => {
            const [datasheetId, recordId, fieldId] = context.split('-');
            res.push({
                eventName: const_1.OPEventNameEnums.CellUpdated,
                context: {
                    datasheetId, recordId, fieldId,
                },
                atomType: const_1.EventAtomTypeEnums.ATOM,
                realType: const_1.EventRealTypeEnums.VIRTUAL,
                sourceType: const_1.EventSourceTypeEnums.ALL,
                scope: this.scope,
            });
        });
        return res;
    }
}
exports.OPEventCellUpdated = OPEventCellUpdated;
//# sourceMappingURL=cell_updated.js.map